import {
  AfterViewInit,
  Component,
  computed,
  input,
  OnChanges,
  OnDestroy,
  output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { Modal } from 'flowbite';
import { Select2ScrollEvent, Select2SearchEvent } from 'ng-select2-component';
import { Subscription } from 'rxjs';
import { APP } from 'src/app/config/app';
import {
  getMessageForm,
  getNewMessageForm,
  ReceiverMessageData,
} from 'src/app/forms/messageForms';
import { IMessage } from 'src/app/models';
import {
  CommunicationUserType,
  InputType,
  MessageChannel,
  NotificationTypes,
} from 'src/app/models/enums';
import { MessageReceiver } from 'src/app/models/enums/messageReceiver';
import {
  LiteralService,
  MessageService,
  RouteService,
  StorageService,
  TripsService,
  UsersService,
  UtilsService,
} from 'src/app/services';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';

@Component({
  selector: 'app-message-details',
  templateUrl: './message-details.component.html',
  styleUrl: './message-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MessageDetailsComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  message = input<IMessage>();
  readonly newMessage = input<Boolean>();
  readonly editMessage = input<Boolean>();
  readonly communicationUserType = input<CommunicationUserType>();
  readonly closeDetails = output();
  readonly refreshMessages = output();
  readonly sentDate = computed(() => {
    const dateTime = this.message()?.dateTime;
    return dateTime ? this.utilsService.convertDate(dateTime) : null;
  });

  public messageForm: any;
  public messageFormControl: FormGroup;
  public invalid: any[] = [];

  public steps = [1];
  public currentStep: number = 1;
  public numberSteps: number = 3;

  public searchValue = '';

  public totalSelectedUsers = 0;

  public exceedReceiversText = ' ';

  public exceedReceiversModal: Modal;
  public exceedReceiversModalShowed = false;

  public additionalCostModal: Modal;
  public additionalCostModalShowed = false;

  public routerSubscription: Subscription;

  public images = images;
  public InputType = InputType;
  public TooltipPosition = TooltipPosition;
  public MessageReceiver = MessageReceiver;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    public messageService: MessageService,
    public usersService: UsersService,
    public storageService: StorageService,
    public serviceInfoService: ServiceInfoService,
    public routeService: RouteService,
    public tripsService: TripsService,
    public router: Router,
  ) {}

  async ngAfterViewInit(): Promise<void> {
    this.messageForm = await getMessageForm(
      this.message()!,
      this.literalService,
      this.storageService,
    );

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.closeModals();
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['newMessage']?.previousValue && !changes['newMessage'].currentValue) this.resetWizard();
    if (this.newMessage()) {
      this.updateMessageForm(undefined, [changes]);
    } else if (this.message()) {
      this.messageForm = await getMessageForm(
        this.message()!,
        this.literalService,
        this.storageService,
      );
    }
  }

  closeModals() {
    if (this.exceedReceiversModalShowed) this.exceedReceiversModal.toggle();
    if (this.additionalCostModalShowed) this.additionalCostModal.toggle();
  }

  async updateMessageForm(
    userMessageData?: ReceiverMessageData,
    changes?: any[],
    step?: number,
  ) {
    this.messageForm = await getNewMessageForm(
      this.message()!,
      this.literalService,
      this.usersService,
      this.storageService,
      this.utilsService,
      this.serviceInfoService,
      this.routeService,
      this.tripsService,
      step ?? this.currentStep,
      this.communicationUserType()!,
      userMessageData,
      changes,
    );
    this.numberSteps = Math.max(
      ...this.messageForm.flatMap((element: any) => element.step || 0),
    );
  }

  continue() {
    if (!this.steps.includes(2) && this.currentStep === 1) {
      !this.steps.includes(2) && this.steps.push(2);
      this.currentStep = 2;
      this.updateMessageForm();
    } else if (!this.steps.includes(3) && this.currentStep === 2) {
      !this.steps.includes(3) && this.steps.push(3);
      this.currentStep = 3;
      this.message()!['receiverIds'] = [];
      this.message()!['receiverServiceId'] = undefined;
      this.message()!['receiverRouteId'] = undefined;
      this.updateMessageForm(undefined, ['receiver']);
    }
  }

  async goBack() {
    await this.updateMessageForm(undefined, undefined, this.currentStep - 1);
    this.currentStep = this.currentStep - 1;
    this.steps.pop();
  }

  public close() {
    this.resetWizard();
    this.closeDetails.emit();
  }

  private resetWizard() {
    this.currentStep = 1;
    this.numberSteps = 3;
    this.steps = [1];
  }

  cancel() {
    this.close();
  }

  public initFormControl() {
    if (this.newMessage()) {
      if (this.numberSteps === 3 && this.currentStep === 1) {
        this.messageFormControl = new FormGroup({
          client: new FormControl(this.message()!.client, [
            Validators.required,
          ]),
        });
      } else if (this.currentStep === this.numberSteps - 1) {
        this.messageFormControl = new FormGroup({
          to: new FormControl(this.message()!.receiver, [Validators.required]),
        });
      } else {
        this.messageFormControl = new FormGroup({
          receiverIds: new FormControl(this.message()!.receiver, [
            Validators.required,
          ]),
          type: new FormControl(this.message()!.channels, [
            Validators.required,
          ]),
          content: new FormControl(this.message()!.content, [
            Validators.required,
          ]),
        });
        if (this.message()!.receiver === MessageReceiver.EXPEDITION) {
          this.messageFormControl.addControl(
            'toServiceIDs',
            new FormControl(
              this.message()!.receiverServiceId,
              Validators.required,
            ),
          );
        }
      }
    }
  }

  public async findInvalidControls() {
    this.initFormControl();
    this.invalid = [];
    const controls = this.messageFormControl.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        this.invalid.push(name);
      }
    }
  }

  disabledSaveButton() {
    if (this.messageForm) {
      this.initFormControl();
      return !this.messageFormControl.valid;
    }
    return true;
  }

  showError(value: string, field: string) {
    let errorText = '';
    this.invalid.map((invalidField: string) => {
      if (invalidField === field) {
        if (
          this.message() &&
          !this.message()![invalidField as keyof IMessage]
          // || this.message()![invalidField as keyof IMessage]!.length === 0
        ) {
          errorText = this.literalService.get('errors.requiredField', true);
        } else {
          errorText = this.literalService.get('errors.incorrectField', true);
        }
      }
    });
    return errorText;
  }

  async changeData(data: any, element?: any) {
    if (data !== undefined && data !== '') {
      const valueToChange: keyof IMessage = element?.edit.valueToChange;
      switch (valueToChange) {
        case 'channels':
          break;
        case 'receiverServiceId':
        case 'receiverRouteId':
          this.message()![valueToChange] = data;
          this.updateMessageForm(undefined, [valueToChange]);
          break;
        case 'receiver':
        case 'receiverIds':
        case 'client':
          this.message()![valueToChange] = data;
          if (element) element.value = data;
          break;
        default:
          (this.message() as any)[valueToChange] = data.value;
          break;
      }
    }
  }

  async scroll(event: Select2ScrollEvent, valueToChange: string) {
    if (event.way === 'down') {
      if (event.search !== '') {
        this.updateMessageForm(
          new ReceiverMessageData({
            showMore: true,
            search: true,
            value: event.search,
          }),
          [valueToChange],
        );
      } else {
        this.updateMessageForm(
          new ReceiverMessageData({ search: false, showMore: true }),
          [valueToChange],
        );
      }
    }
  }

  async search(event: Select2SearchEvent, valueToChange: string) {
    this.searchValue = event.search;
    if (
      valueToChange === 'receiverIds' ||
      valueToChange === 'receiverServiceId' ||
      valueToChange === 'receiverRouteId'
    ) {
      const dropDown = document.getElementsByClassName(
        'select2-results__options',
      )[0];
      if (this.searchValue !== '') {
        this.updateMessageForm(
          new ReceiverMessageData({
            showMore: false,
            search: true,
            value: event.search,
          }),
          [valueToChange],
        );
        dropDown.scroll({
          top: 0,
        });
      } else {
        this.updateMessageForm(
          new ReceiverMessageData({
            search: false,
          }),
          [valueToChange],
        );
      }
    }
  }

  async checkChannelsLimitations() {
    await this.findInvalidControls();
    if (this.invalid.length === 0) {
      const receiverUsers = await this.messageService.getMessageReceivers(
        this.message()!,
        this.communicationUserType()!,
      );
      this.totalSelectedUsers = receiverUsers.pagination.totalElements;
      if (
        (this.message()?.channels.some(
          (channel: any) => channel.id === MessageChannel.EMAIL,
        ) &&
          this.totalSelectedUsers > APP.MAXQUEUE_EMAIL) ||
        (this.message()?.channels.some(
          (channel: any) => channel.id === MessageChannel.PUSH_NOTIFICATION,
        ) &&
          this.totalSelectedUsers > APP.MAXQUEUE_PUSHNOTIFICATION) ||
        (this.message()?.channels.some(
          (channel: any) => channel.id === MessageChannel.SMS,
        ) &&
          this.totalSelectedUsers > APP.MAXQUEUE_SMS)
      ) {
        this.exceedReceiversText = `
          ${this.literalService.get('messages.modal.exceedReceivers.usersSelected', true)}: <strong>${this.totalSelectedUsers}</strong>

          ${this.literalService.get('messages.modal.exceedReceivers.limitDescription', true)}:${this.message()?.channels.some((channel: any) => channel.id === MessageChannel.PUSH_NOTIFICATION) ? this.getMaxQueueTextByChannel(MessageChannel.PUSH_NOTIFICATION) : ''}${this.message()?.channels.some((channel: any) => channel.id === MessageChannel.EMAIL) ? this.getMaxQueueTextByChannel(MessageChannel.EMAIL) : ''}${this.message()?.channels.some((channel: any) => channel.id === MessageChannel.SMS) ? this.getMaxQueueTextByChannel(MessageChannel.SMS) : ''}

          ${this.literalService.get('messages.modal.exceedReceivers.description', true)}
        `;
        this.exceedReceiversModal = new Modal(
          document.getElementById('exceedReceiversModal'),
          {
            placement: 'center',
            closable: false,
            onHide: () => (this.exceedReceiversModalShowed = false),
            onShow: () => (this.exceedReceiversModalShowed = true),
          },
        );
        this.exceedReceiversModal.show();
      } else {
        this.checkAdditionalCost();
      }
    }
  }

  getMaxQueueTextByChannel(channel: MessageChannel) {
    let maxNumber = 0;
    switch (channel) {
      case MessageChannel.EMAIL:
        maxNumber = APP.MAXQUEUE_EMAIL;
        break;
      case MessageChannel.PUSH_NOTIFICATION:
        maxNumber = APP.MAXQUEUE_PUSHNOTIFICATION;
        break;
      case MessageChannel.SMS:
        maxNumber = APP.MAXQUEUE_SMS;
        break;
    }
    return `\n<strong>${maxNumber}</strong> ${this.literalService.get('messages.modal.exceedReceivers.usersBy')} ${this.literalService.get(`messages.channel.${channel.toLowerCase()}`)}`;
  }

  checkAdditionalCost() {
    this.exceedReceiversModal?.hide();
    if (
      this.message()?.channels.some(
        (channel: any) => channel.id === MessageChannel.SMS,
      )
    ) {
      this.additionalCostModal = new Modal(
        document.getElementById('additionalCostModal'),
        {
          placement: 'center',
          closable: false,
          onHide: () => (this.additionalCostModalShowed = false),
          onShow: () => (this.additionalCostModalShowed = true),
        },
      );
      this.additionalCostModal.show();
    } else this.onSubmit();
  }

  onSubmit() {
    this.additionalCostModal?.hide();
    this.messageService
      .createMessage(this.message()!, this.communicationUserType()!)
      .then(() => {
        this.utilsService.showNotification(
          images.notifications.check,
          `messages.actions.create`,
          NotificationTypes.SUCCESS,
        );
        this.refreshMessages.emit();
        this.close();
      })
      .catch(() => {
        this.utilsService.showNotification(
          images.notifications.warning,
          `messages.actions.createError`,
          NotificationTypes.DANGER,
        );
      });
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
  }
}
